import { useEffect, useState } from "react";
import { useApp } from "@/app/container";
import { useQuery } from "@/app/hooks/useQuery";
import { useLocale } from "@/app/i18n/LocaleProvider";
import { format } from "@/app/i18n/strings";
import type { ReminderState } from "@/services/ReminderService";
import { Button } from "../Button";
import { BROWSER_KEYS, guessBrowser, type BrowserKey } from "./browserGuide";
import styles from "./reminders.module.css";
import settingsStyles from "@/app/screens/SettingsScreen.module.css";

/**
 * Settings section that owns the browser permission for reminders. The first
 * tap only warns about the browser's own dialog; the second tap opens it. A
 * refusal turns the section into a step-by-step guide for the user's browser.
 */
export function RemindersSection({ state, reload }: { state: ReminderState | undefined; reload: () => void }) {
  const app = useApp();
  const { t } = useLocale();
  const [warned, setWarned] = useState(false);
  const [guide, setGuide] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [browser, setBrowser] = useState<BrowserKey>(() => guessBrowser(navigator.userAgent, navigator.maxTouchPoints > 1));

  // Coming back from the browser's settings: read the permission again.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") reload();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reload]);

  // What the device really holds, read back after a sync; null on the web, where the server sends reminders.
  const upcoming = useQuery(() => (state === "enabled" ? app.reminders.upcoming() : Promise.resolve(null)), [state], app.changeSources);
  useEffect(() => {
    if (!upcoming.data) return;
    app.analytics.track("reminders_upcoming", { count: upcoming.data.length, error: app.reminders.lastError?.slice(0, 120) ?? "" });
  }, [app, upcoming.data]);

  // The server key is fetched ahead of the tap so nothing waits between the browser's dialog and the subscription.
  useEffect(() => {
    void app.reminders.prepare().catch(() => undefined);
  }, [app]);

  const enable = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const next = await app.reminders.enable();
      app.analytics.track(next === "enabled" ? "reminders_enabled" : "reminders_refused", { state: next, error: app.reminders.lastError?.slice(0, 120) ?? "" });
      setWarned(false);
      if (next === "denied") setGuide(true);
      reload();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  const pause = async () => {
    setBusy(true);
    try {
      await app.reminders.pause();
      app.analytics.track("reminders_paused");
      reload();
    } finally {
      setBusy(false);
    }
  };

  if (state === undefined) return null;
  const host = window.location.hostname || "u-memo.com";
  const r = t.reminders;

  return (
    <section className={settingsStyles.section}>
      <h2 className={settingsStyles.heading}>{r.title}</h2>
      <div className={styles.card}>
        {state === "unsupported" && <p className={styles.muted}>{r.unsupported}</p>}

        {state === "needs-install" && (
          <>
            <p className={styles.muted}>{r.needsInstall}</p>
            <ol className={styles.steps}>{r.installSteps.map((step) => <li key={step}>{step}</li>)}</ol>
            <p className={styles.muted}>{r.transferHint}</p>
          </>
        )}

        {(state === "prompt" || state === "granted") && (
          <>
            <p className={styles.muted}>{r.intro}</p>
            {warned && state === "prompt" && <p className={styles.warning} role="note">{r.warning}</p>}
            <div className={styles.actions}>
              {state === "prompt" && !warned ? (
                <Button variant="primary" disabled={busy} onClick={() => { app.analytics.track("reminders_enable_click"); setWarned(true); }}>{r.enable}</Button>
              ) : (
                <Button variant="primary" disabled={busy} onClick={() => void enable()}>{state === "prompt" ? r.showPrompt : r.enable}</Button>
              )}
            </div>
          </>
        )}

        {state === "enabled" && (
          <>
            <div className={styles.actions}>
              <span>{r.enabled}</span>
              <Button variant="ghost" disabled={busy} onClick={() => void pause()}>{r.disable}</Button>
            </div>
            {upcoming.data && upcoming.data.length === 0 && <p className={styles.muted}>{r.nothingScheduled}</p>}
            {upcoming.data?.map((reminder) => (
              <p key={reminder.id} className={styles.muted}>{reminder.title} · {format(r.dailyAt, { time: reminder.time })}</p>
            ))}
          </>
        )}

        {state === "paused" && (
          <div className={styles.actions}>
            <span className={styles.muted}>{r.paused}</span>
            <Button variant="primary" disabled={busy} onClick={() => void enable()}>{r.enable}</Button>
          </div>
        )}

        {state === "denied" && (
          <>
            <p className={styles.muted}>{r.denied}</p>
            <div className={styles.actions}>
              {!guide && <Button variant="primary" onClick={() => { app.analytics.track("reminders_guide_open"); setGuide(true); }}>{r.howToAllow}</Button>}
              <Button variant="ghost" disabled={busy} onClick={reload}>{r.retry}</Button>
            </div>
            {guide && (
              <>
                <label className={styles.rowField}>
                  {r.guideBrowser}
                  <select className={styles.select} value={browser} onChange={(e) => setBrowser(e.target.value as BrowserKey)}>
                    {BROWSER_KEYS.map((key) => <option key={key} value={key}>{r.browsers[key]}</option>)}
                  </select>
                </label>
                <ol className={styles.steps}>{r.guides[browser].map((step) => <li key={step}>{format(step, { host })}</li>)}</ol>
                <p className={styles.muted}>{r.guideAfter}</p>
              </>
            )}
          </>
        )}

        {failed && <p className={styles.error} role="alert">{r.failed}</p>}
        {app.reminders.lastError && <p className={styles.muted}>{app.reminders.lastError}</p>}
      </div>
    </section>
  );
}
